import {
  addInstructorToClub,
  removeInstructorFromClub,
} from '@/api/clubs/clubApi';
import ConfirmRemoveInstructorDialog from '@/components/clubs/ConfirmRemoveInstructorDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import { CLUB_STATUS } from '@/types/club.model';
import type { Instructor } from '@/types/club.model';
import { handleGlobalError } from '@/utils/error-handler';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import {
  AlertCircle,
  CheckCircle,
  ChevronLeft,
  GraduationCap,
  Info,
  Mail,
  Trash2,
  UserPlus,
  Users,
} from 'lucide-react';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';

const inviteInstructorSchema = z.object({
  email: z
    .string()
    .min(1, 'El correo electrónico es requerido')
    .email('Ingresa un correo electrónico válido')
    .max(255, 'El correo electrónico es demasiado largo'),
});

type InviteInstructorFormData = z.infer<typeof inviteInstructorSchema>;

const InviteInstructor: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { clubId, clubName, clubStatus, clubInstructors, canRemoveInstructors } =
    location.state || {};

  const isDisabled = clubStatus === CLUB_STATUS.DISABLED;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [instructorsToRemove, setInstructorsToRemove] = useState<string[]>([]);
  const [currentInstructors, setCurrentInstructors] = useState<Instructor[]>(
    clubInstructors || [],
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedInstructor, setSelectedInstructor] =
    useState<Instructor | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    watch,
  } = useForm<InviteInstructorFormData>({
    resolver: zodResolver(inviteInstructorSchema),
    mode: 'onChange',
    defaultValues: {
      email: '',
    },
  });

  const emailValue = watch('email');

  const onSubmit = async (data: InviteInstructorFormData) => {
    if (!clubId) {
      toast({
        title: 'Error',
        description: 'No se pudo identificar el club.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsSubmitting(true);
      await addInstructorToClub(clubId, data.email);

      toast({
        title: 'Invitación enviada',
        description: `Se ha invitado exitosamente a ${data.email} como profesor del club.`,
        variant: 'default',
      });

      reset();

      setTimeout(() => {
        navigate('/clubs');
      }, 2000);
    } catch (error) {
      handleGlobalError(
        error,
        'Error al enviar invitación',
        'No se pudo enviar la invitación. Intentá nuevamente más tarde.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveInstructor = async (instructorEmail: string) => {
    if (!clubId) {
      toast({
        title: 'Error',
        description: 'No se pudo identificar el club.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setInstructorsToRemove((prev) => [...prev, instructorEmail]);
      await removeInstructorFromClub(clubId, instructorEmail);

      setCurrentInstructors((prev) =>
        prev.filter((instructor) => instructor.email !== instructorEmail),
      );

      toast({
        title: 'Profesor eliminado',
        description: `Se ha eliminado exitosamente a ${instructorEmail} como profesor del club.`,
        variant: 'default',
      });
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      toast({
        title: 'Error al eliminar profesor',
        description:
          axiosError.response?.data?.message ||
          'No se pudo eliminar el profesor.',
        variant: 'destructive',
      });
    } finally {
      setInstructorsToRemove((prev) =>
        prev.filter((email) => email !== instructorEmail),
      );
    }
  };

  const openRemoveDialog = (instructor: Instructor) => {
    setSelectedInstructor(instructor);
    setDialogOpen(true);
  };

  const handleConfirmRemove = () => {
    if (selectedInstructor) {
      handleRemoveInstructor(selectedInstructor.email);
    }
  };

  const handleBack = () => {
    navigate('/clubs');
  };

  if (!clubId || !clubName) {
    return <Navigate to="/clubs" replace />;
  }

  return (
    <div>
      <div className="mb-4 flex items-center gap-2 p-2">
        <Button variant="ghost" onClick={handleBack} className="left-4 top-4">
          <ChevronLeft size={16} />
        </Button>
        <h2 className="text-2xl font-bold">
          Administrar Profesores:{' '}
          <span className="text-blue-600">{clubName}</span>
        </h2>
      </div>
      <div className="container mx-auto max-w-4xl px-4 py-8">
        <div className="grid gap-8">
          <Card className="shadow-lg">
            <CardHeader className="relative text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
                <GraduationCap size={32} className="text-blue-600" />
              </div>
              <CardTitle className="text-2xl font-bold text-gray-900">
                Invitar Profesor
              </CardTitle>
              <p className="mt-2 text-gray-600">
                Club:{' '}
                <span className="font-semibold text-gray-900">{clubName}</span>
              </p>
            </CardHeader>

            <CardContent className="space-y-6">
              {isDisabled && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle
                      size={20}
                      className="mt-0.5 flex-shrink-0 text-red-600"
                    />
                    <div>
                      <h4 className="mb-1 font-semibold text-red-900">
                        Club Deshabilitado
                      </h4>
                      <p className="text-sm text-red-800">
                        No puedes invitar profesores porque el club está
                        actualmente deshabilitado. Para poder invitar
                        profesores, primero debes{' '}
                        <Link
                          to="/clubs"
                          className="font-semibold underline hover:text-red-900"
                        >
                          habilitar el club desde la lista de clubes
                        </Link>
                        .
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                <div className="flex items-start gap-3">
                  <Info
                    size={20}
                    className="mt-0.5 flex-shrink-0 text-blue-600"
                  />
                  <div>
                    <h3 className="mb-2 font-semibold text-blue-900">
                      ¿Qué puede hacer un Profesor?
                    </h3>
                    <p className="text-sm leading-relaxed text-blue-800">
                      El usuario que invites como profesor podrá{' '}
                      <strong>dar clases y gestionar sus lecciones</strong> en tu
                      club, usando las canchas e instalaciones disponibles.
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="space-y-2">
                  <Label
                    htmlFor="email"
                    className="text-sm font-medium text-gray-700"
                  >
                    Correo electrónico del profesor
                  </Label>
                  <div className="relative">
                    <Mail
                      size={18}
                      className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400"
                    />
                    <Input
                      id="email"
                      type="email"
                      {...register('email')}
                      placeholder="ejemplo@correo.com"
                      className={`pl-10 ${errors.email ? 'border-red-500' : ''}`}
                      disabled={isDisabled}
                    />
                    {emailValue && !errors.email && (
                      <CheckCircle
                        size={18}
                        className="absolute right-3 top-1/2 -translate-y-1/2 transform text-green-500"
                      />
                    )}
                  </div>
                  {errors.email && (
                    <p className="text-sm text-red-500">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={!isValid || isSubmitting || isDisabled}
                >
                  {isSubmitting ? (
                    <>
                      <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                      Enviando invitación...
                    </>
                  ) : (
                    <>
                      <UserPlus size={18} className="mr-2" />
                      Invitar como Profesor
                    </>
                  )}
                </Button>
              </form>

              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                <div className="flex items-start gap-3">
                  <Info
                    size={20}
                    className="mt-0.5 flex-shrink-0 text-amber-600"
                  />
                  <div>
                    <h4 className="mb-1 font-semibold text-amber-900">
                      Importante
                    </h4>
                    <p className="text-sm text-amber-800">
                      Si la persona todavía no tiene cuenta, le llega un email
                      para registrarse. Cuando cree su cuenta{' '}
                      <strong>con ese mismo email</strong>, queda agregada como
                      profesor del club.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                <Users size={32} className="text-green-600" />
              </div>
              <CardTitle className="text-2xl font-bold text-gray-900">
                Profesores Actuales
              </CardTitle>
              <p className="mt-2 text-gray-600">
                {currentInstructors.length}{' '}
                {currentInstructors.length === 1
                  ? 'profesor'
                  : 'profesores'}{' '}
                en el club
              </p>
            </CardHeader>

            <CardContent>
              {currentInstructors.length > 0 ? (
                <div className="space-y-3">
                  {currentInstructors.map((instructor) => (
                    <div
                      key={instructor.email}
                      className="flex items-center justify-between gap-3 rounded-lg border bg-gray-50 p-4"
                    >
                      <div className="min-w-0 flex-1">
                        <h4 className="font-semibold text-gray-900">
                          {instructor.name || instructor.username}
                        </h4>
                        <p className="break-all text-sm text-gray-600">
                          {instructor.email}
                        </p>
                        {instructor.name && instructor.username && (
                          <p className="text-xs text-gray-500">
                            {instructor.username}
                          </p>
                        )}
                      </div>

                      {canRemoveInstructors && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-shrink-0 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                          disabled={instructorsToRemove.includes(instructor.email)}
                          onClick={() => openRemoveDialog(instructor)}
                        >
                          {instructorsToRemove.includes(instructor.email) ? (
                            <>
                              <div className="mr-2 h-3 w-3 animate-spin rounded-full border-b-2 border-red-600"></div>
                              Eliminando...
                            </>
                          ) : (
                            <>
                              <Trash2 size={14} className="mr-2" />
                              Eliminar
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center">
                  <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                    <Users size={24} className="text-gray-400" />
                  </div>
                  <p className="text-sm text-gray-500">
                    No hay profesores asignados a este club todavía.
                  </p>
                  <p className="mt-1 text-xs text-gray-400">
                    Invita a alguien para que pueda dar clases en tu club.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <ConfirmRemoveInstructorDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          instructor={selectedInstructor}
          onConfirm={handleConfirmRemove}
          isLoading={
            selectedInstructor
              ? instructorsToRemove.includes(selectedInstructor.email)
              : false
          }
        />
      </div>
    </div>
  );
};

export default InviteInstructor;
